/**
 * galaxy-view.ts — strategic star map renderer.
 *
 * Reads GameState + UiState.galaxyCamera and draws the whole galaxy: stars,
 * their planets (as tiny orbiting dots), and ships (as owner-coloured
 * chevrons). Clips everything to the supplied viewport rectangle.
 *
 * This module is a pure reader — it never mutates state.
 */

import type { EmpireId, GameState, StarClass, StarSystem, SystemId } from '../state/types/index';
import type { UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';
import { WARP_GATE_CHASSIS } from '../game/balance-space-structures';
import { hyperdriveRangeMultiplier } from '../game/research-effects';
import { drawGalaxyOverlay } from './galaxy-overlays';
import { drawGalaxyJumpTargetPicker } from './galaxy-jump-target';
import { drawGalaxyArrivalWarnings } from './galaxy-arrival-warnings';
import { drawGalaxyPaths } from './galaxy-paths';
import { drawGalaxyAdjacency, isFleetReachableModeActive } from './galaxy-adjacency';
import { drawGalaxyLrsaRings } from './galaxy-lrsa-rings';

/**
 * v4M6.1 — per-class star palette. The three classes (red-dwarf / yellow /
 * blue-giant) are derived deterministically from `systemId` at scenario gen
 * + on load (see `state/star-class.ts`). Each class gets:
 *  - `radiusMul`: scales the base zoom-driven star radius. Red dwarfs read
 *    smaller, blue giants larger.
 *  - `core`: filled star body colour.
 *  - `glowInner` / `glowOuter`: the radial-gradient soft glow stops behind
 *    the body. Outer is the same hue with zero alpha so the gradient fades
 *    cleanly into the starfield.
 *  - `corona`: blue giants only — an extra wider, dimmer halo gradient so
 *    the largest class reads as "hotter" at any zoom level.
 */
function starStyleForClass(c: StarClass): {
  radiusMul: number;
  core: string;
  glowInner: string;
  glowOuter: string;
  corona: boolean;
} {
  // MFix36 (2026-05-30) — palette retuned to "fantasy/4X" set per
  // playtest feedback: the prior pinkish red-dwarf read as a danger
  // signal, the pale blue-giant read as ambient grey. Ruby / gold /
  // cyan-teal keep each class unambiguously distinct from UI greys
  // and from the existing red "hostile" / amber "rejection" toast
  // palette. Class labels remain astrophysical ("Blue Giant") — the
  // hue is a visual representation, not a rename.
  switch (c) {
    case 'red-dwarf':
      return {
        radiusMul: 0.75,
        core: '#e84545',
        glowInner: 'rgba(232, 69, 69, 0.35)',
        glowOuter: 'rgba(232, 69, 69, 0)',
        corona: false,
      };
    case 'yellow':
      return {
        radiusMul: 1.0,
        core: '#fcd34d',
        glowInner: 'rgba(252, 211, 77, 0.35)',
        glowOuter: 'rgba(252, 211, 77, 0)',
        corona: false,
      };
    case 'blue-giant':
      return {
        radiusMul: 1.25,
        core: '#06b6d4',
        glowInner: 'rgba(6, 182, 212, 0.45)',
        glowOuter: 'rgba(6, 182, 212, 0)',
        corona: true,
      };
  }
}

/**
 * v4M6.1 — return the empire id that owns the most planets in `sys`,
 * or `null` if no planets are owned. Ties broken by lowest empire id
 * for determinism across frames. Used by the per-system owner halo
 * so a system reads as "blue empire's territory" at a glance without
 * the player having to interpret per-planet dots.
 */
function dominantOwnerEmpireId(
  state: GameState,
  sys: StarSystem,
): EmpireId | null {
  const counts = new Map<EmpireId, number>();
  for (const pid of sys.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet || planet.ownerEmpireId === null) continue;
    counts.set(planet.ownerEmpireId, (counts.get(planet.ownerEmpireId) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  let bestId: EmpireId | null = null;
  let bestCount = -1;
  for (const [id, count] of [...counts.entries()].sort((a, b) => a[0] - b[0])) {
    if (count > bestCount) {
      bestCount = count;
      bestId = id;
    }
  }
  return bestId;
}

export function drawGalaxyView(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
): void {
  ctx.save();
  // Clip galaxy drawing to the viewport so pans beyond the edge don't spill
  // over the HUD or panel.
  ctx.beginPath();
  ctx.rect(vp.padLeft, vp.padTop, vp.viewW, vp.viewH);
  ctx.clip();

  const selectedSysId = ui.selection.kind === 'system' ? ui.selection.id : null;

  // MFix6.3 — Galaxy-view fog-of-war: planet dots + system labels +
  // (in MFix6.4) per-empire icon stacks are gated on the viewer empire's
  // `scoutedSystemIds`. Computed once per frame; reused across the
  // system loop and the aggregate-icon loop. devRevealAll (F10 cheat)
  // bypasses the gate to make every system visible.
  const viewerEmpireForFog = state.empires.get(viewerEmpireId(state, ui));
  const isScouted = (sysId: SystemId): boolean =>
    ui.devRevealAll ? true : viewerEmpireForFog?.scoutedSystemIds.has(sysId) ?? false;

  // Sprite sizes — scale gently with zoom so systems feel weighty when zoomed
  // in but stay legible at minimum zoom.
  // MFix6.6 — star + planet-orbit bumped for galaxy-view readability.
  // Initial 10× was too aggressive in playtest; halved to ~5× the
  // pre-MFix6.6 baseline. SYSTEM_HIT_RADIUS_PX in input.ts matches.
  const starRadius = Math.max(25, 35 * Math.min(1.5, ui.galaxyCamera.zoom));
  const planetOrbitRadius = Math.max(90, 110 * Math.min(2, ui.galaxyCamera.zoom));
  const planetDotRadius = 5;

  // v4M6.1 — orbital signature rings (one per planet, faint dotted) get
  // suppressed at low zoom to keep the galaxy map readable when systems
  // are crowded onto a small screen area. Threshold picked so the rings
  // appear shortly after the star floor lifts off its minimum.
  const showOrbitalRings = ui.galaxyCamera.zoom > 0.6;

  // v4M5.3b3e — range-cap dimming. When the player has ships selected,
  // compute the source system + fleet effective hyperdrive range and
  // mark every system beyond that distance from the source as
  // out-of-range. Out-of-range systems render with a desaturated glow
  // + reduced star alpha so the player can see at a glance which
  // destinations the current selection can reach.
  //
  // v4M6.4 — the negative out-of-range fog is suppressed when the
  // fleet-reachable adjacency overlay is drawing the positive
  // indicator (lines TO reachable systems). Per `MAP.md §5.3` the
  // overlay replaces the fog rather than layering on top.
  const outOfRange = isFleetReachableModeActive(state, ui)
    ? new Set<SystemId>()
    : computeOutOfRangeSystemIds(state, ui);

  // v4M6.4 — selection-driven adjacency overlay (replaces the v4M5.3b3c
  // always-on hyperlane wireframe). No-op in the default no-selection
  // state. Drawn before Warp Gates so the always-on Warp Gate
  // infrastructure layers on top of the transient selection cue.
  drawGalaxyAdjacency(ctx, state, ui, vp);

  // MFix31.1 — viewer + teammate LRSA detection rings per playtest
  // feedback on v4M10.6 (cross-system reveal had no in-game radius cue).
  // Soft empire-tinted stroke; sub-pixel rings cull at low zoom. Drawn
  // before warp gates / overlays / ships so the more transient cues
  // layer on top.
  drawGalaxyLrsaRings(ctx, state, ui, vp);

  // v3M18 — Warp Gate connections. Owner-only red lines drawn after
  // standard JPs so they read clearly when both exist.
  // MFix30.3 — visual differentiation pass: live links render as
  // SOLID 2.5 px red strokes; building links keep the v3M18 dashed
  // [6, 4] pattern at 1.5 px so progress reads at-a-glance. After
  // the v4M6.4 lane removal the warp-gate lines became the only
  // always-on inter-system signal — switching live links to solid
  // surfaces them as deliberate player-built infrastructure rather
  // than leftover travel cues. Empire-coloured selection-driven
  // dotted lines (v4M6.4) stay distinct via dash + colour + alpha.
  // Perpendicular offset preserved for legacy natural-hyperlane
  // collisions (the offset is harmless after v4M6.4 since hyperlanes
  // no longer render; kept for future re-introduction or symmetry
  // with two parallel warp links between the same pair of systems).
  ctx.save();
  for (const link of state.warpGateLinks.values()) {
    if (
      !ui.devRevealAll &&
      link.ownerEmpireId !== viewerEmpireForFog?.id
    ) {
      continue;
    }
    const aSys = state.systems.get(link.systemAId);
    const bSys = state.systems.get(link.systemBId);
    if (!aSys || !bSys) continue;
    const aP = worldToScreen(ui.galaxyCamera, vp, { x: aSys.x, y: aSys.y });
    const bP = worldToScreen(ui.galaxyCamera, vp, { x: bSys.x, y: bSys.y });

    // v4M5.3b3c — detect collision with a natural hyperlane between
    // the same two systems via the system-pair adjacency list.
    const hasJpCollision = aSys.adjacentSystemIds.includes(link.systemBId);

    let ax = aP.x;
    let ay = aP.y;
    let bx = bP.x;
    let by = bP.y;
    if (hasJpCollision) {
      const dx = bP.x - aP.x;
      const dy = bP.y - aP.y;
      const len = Math.hypot(dx, dy);
      if (len > 0.001) {
        // Perpendicular unit vector × 5 px offset.
        const px = (-dy / len) * 5;
        const py = (dx / len) * 5;
        ax += px;
        ay += py;
        bx += px;
        by += py;
      }
    }

    if (link.status === 'live') {
      // MFix30.3 — solid, thicker, full-red for completed infrastructure.
      ctx.strokeStyle = 'rgba(220, 60, 60, 0.75)';
      ctx.lineWidth = 2.5;
      ctx.setLineDash([]);
    } else {
      // Building: v3M18 dashed style retained so in-progress links
      // read as "not yet there" distinct from live solid lines.
      ctx.strokeStyle = 'rgba(220, 60, 60, 0.35)';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
    }
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();

  // v3M18 — precompute valid Connect Warp Gate target systems when the
  // player is in connect-mode. A target system is one that contains a
  // same-empire operational unconnected Warp Gate AND is not the
  // origin system (no self-links). Computed once per frame and reused
  // in the per-system loop below.
  const connectTargetSystems = new Set<SystemId>();
  if (ui.connectWarpGateMode) {
    const fromGateId = ui.connectWarpGateMode.fromGateId;
    const fromSystemId = ui.connectWarpGateMode.fromSystemId;
    const fromGate = state.ships.get(fromGateId);
    if (fromGate) {
      const ownerId = fromGate.ownerEmpireId;
      // Build a "this gate is already an endpoint of some link" lookup.
      const linkedGateIds = new Set<number>();
      for (const link of state.warpGateLinks.values()) {
        linkedGateIds.add(link.gateAId);
        linkedGateIds.add(link.gateBId);
      }
      for (const s of state.ships.values()) {
        if (s.id === fromGateId) continue;
        if (s.ownerEmpireId !== ownerId) continue;
        if (s.chassis !== WARP_GATE_CHASSIS) continue;
        if (s.systemId === null || s.systemId === fromSystemId) continue;
        if (linkedGateIds.has(s.id)) continue;
        connectTargetSystems.add(s.systemId);
      }
    }
  }

  for (const sys of state.systems.values()) {
    const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
    const isSelected = sys.id === selectedSysId;
    const isConnectTarget = connectTargetSystems.has(sys.id);
    // v4M5.3b3e — out-of-range systems get a desaturated glow + half
    // alpha on the star body so the player can see at a glance which
    // destinations the current fleet selection can reach.
    const isOutOfRange = outOfRange.has(sys.id);
    const scouted = isScouted(sys.id);

    // v4M6.1 — per-class star style + per-system effective radius. Class
    // is derived from system id (state/star-class.ts) so blue giants stay
    // blue giants across saves without persisting the value. Selection
    // and connect-mode visuals use `rDraw` rather than the base radius
    // so a blue-giant selection ring sits outside the class-scaled body.
    const style = starStyleForClass(sys.starClass);
    const rDraw = starRadius * style.radiusMul;
    // v4M6.5 — unscouted systems collapse the class-coloured glow / corona
    // / owner halo / per-class body fill into a neutral grey-white sphere
    // per `docs/MAP.md §6.3`. Out-of-range stars already use this neutral
    // fallback (v4M5.3b3e); unscouted joins the same path so the player
    // can't infer class identity ahead of scouting.
    const useNeutralStar = !scouted || isOutOfRange;

    // Soft glow behind the star — class-tinted when scouted + in range;
    // neutral cool grey when unscouted or out-of-range so the dim cue
    // still reads against any class colour.
    const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, rDraw * 3);
    if (useNeutralStar) {
      glow.addColorStop(0, 'rgba(120, 140, 160, 0.18)');
      glow.addColorStop(1, 'rgba(120, 140, 160, 0)');
    } else {
      glow.addColorStop(0, style.glowInner);
      glow.addColorStop(1, style.glowOuter);
    }
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(p.x, p.y, rDraw * 3, 0, Math.PI * 2);
    ctx.fill();

    // v4M6.1 — blue-giant corona: an extra wider, dimmer outer ring of
    // glow so the largest class reads as "hotter" at any zoom level.
    // Suppressed when out-of-range OR unscouted to keep the neutral
    // treatment consistent.
    if (style.corona && !useNeutralStar) {
      const corona = ctx.createRadialGradient(
        p.x, p.y, rDraw * 2,
        p.x, p.y, rDraw * 4,
      );
      corona.addColorStop(0, 'rgba(180, 220, 255, 0.18)');
      corona.addColorStop(1, 'rgba(180, 220, 255, 0)');
      ctx.fillStyle = corona;
      ctx.beginPath();
      ctx.arc(p.x, p.y, rDraw * 4, 0, Math.PI * 2);
      ctx.fill();
    }

    // v4M6.1 — owner halo. Soft 1.5×-star-radius gradient ring in the
    // dominant planet-owner empire's colour, giving the player a
    // glanceable "this is blue empire's territory" cue. Scouted-gated
    // so unowned territory doesn't leak ownership through the halo;
    // out-of-range suppressed for the same reason as the corona.
    if (scouted && !isOutOfRange) {
      const ownerId = dominantOwnerEmpireId(state, sys);
      if (ownerId !== null) {
        const ownerColor = state.empires.get(ownerId)?.color;
        if (ownerColor) {
          const halo = ctx.createRadialGradient(
            p.x, p.y, rDraw,
            p.x, p.y, rDraw * 1.5,
          );
          // Empire colours are #RRGGBB; append 8-bit alpha to fade.
          halo.addColorStop(0, ownerColor + '55');
          halo.addColorStop(1, ownerColor + '00');
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(p.x, p.y, rDraw * 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // v4M6.1 — selection ring now pulses (~0.5 s period at 10 Hz) to
    // reinforce focus without distracting from idle systems. Radius is
    // tied to `rDraw` so a selected blue giant gets a proportionally
    // wider ring than a selected red dwarf.
    if (isSelected) {
      const pulse = 1 + 0.08 * Math.sin(state.tick * 1.26);
      ctx.beginPath();
      ctx.strokeStyle = '#8fffb0';
      ctx.lineWidth = 2;
      ctx.arc(p.x, p.y, (rDraw + 8) * pulse, 0, Math.PI * 2);
      ctx.stroke();
    }

    // v3M18 — Connect Warp Gate target ring. Pulsing red (animated via
    // tick parity to give a subtle "click me" feedback without holding
    // a separate animation state). Drawn on top of selection so the
    // player can still see which system was the origin.
    if (isConnectTarget) {
      const pulse = 1 + 0.15 * Math.sin(state.tick * 0.2);
      ctx.save();
      ctx.strokeStyle = 'rgba(220, 60, 60, 0.85)';
      ctx.lineWidth = 2.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(p.x, p.y, (rDraw + 14) * pulse, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // v4M6.1 — star body uses the per-class core colour. v4M6.5 —
    // unscouted joins the out-of-range neutral grey path, so both
    // states render as a dim grey-white sphere per `MAP.md §6.3`.
    // Out-of-range alpha-dims to 0.45 to convey "can't reach"; unscouted
    // doesn't dim further (the position cue needs to stay visible for
    // dispatch). Selected stars get a faint white wash only when the
    // class colour shows through — neutral stars stay neutral so the
    // selection ring carries the focus signal alone.
    ctx.save();
    if (isOutOfRange) ctx.globalAlpha = 0.45;
    ctx.fillStyle = useNeutralStar ? '#9aa6bd' : style.core;
    ctx.beginPath();
    ctx.arc(p.x, p.y, rDraw, 0, Math.PI * 2);
    ctx.fill();
    if (isSelected && !useNeutralStar) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.beginPath();
      ctx.arc(p.x, p.y, rDraw, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Label. MFix6.3 — unscouted systems show "?" in place of their
    // name so the player can navigate the galaxy without leaking which
    // is which. Scouting (any empire ship enters the system) reveals
    // the name permanently. MFix6.6 fix-up — font bumped 12 → 20 px.
    // MFix6.6 fix-up #2 — moved from right of the star to above it so the
    // aggregate-icon column to the right has the full vertical span free
    // of label collision. Centre-aligned with bottom baseline puts the
    // text exactly above the star edge. v4M6.1 — offset by per-class
    // rDraw so labels track blue-giant body size, not the base radius.
    ctx.save();
    ctx.fillStyle = isSelected ? '#d8e4ff' : '#9fb3d9';
    ctx.font = '20px Inter, system-ui, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText(scouted ? sys.name : '?', p.x, p.y - rDraw - 6);
    ctx.restore();

    // v4M6.1 — per-system orbital signature: one faint dotted ring
    // per planet, distributed radially between just outside the star
    // and the outer planet-orbit band. Combined with the planet dots
    // below, this gives the player a glanceable read on "how rich is
    // this system?" without opening system view. Scouted-gated +
    // suppressed at low zoom (rings get cluttered when stars are
    // near their floor size).
    if (scouted) {
      const planets = sys.planetIds;
      const n = planets.length;
      if (n > 0) {
        const rInner = rDraw * 1.8;
        const rOuter = planetOrbitRadius;
        const ringR = (i: number): number =>
          n === 1
            ? (rInner + rOuter) / 2
            : rInner + ((rOuter - rInner) * i) / (n - 1);

        if (showOrbitalRings) {
          ctx.save();
          ctx.strokeStyle = 'rgba(150, 170, 200, 0.18)';
          ctx.lineWidth = 1;
          ctx.setLineDash([2, 4]);
          for (let i = 0; i < n; i++) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, ringR(i), 0, Math.PI * 2);
            ctx.stroke();
          }
          ctx.setLineDash([]);
          ctx.restore();
        }

        // Planet dots — each sits on its own ring's radius at a
        // deterministic angle so the same system reads the same way
        // across frames. v4M6.1 — distributed radially, not all on
        // the outermost ring like before.
        for (let i = 0; i < n; i++) {
          const planet = state.planets.get(planets[i]!);
          if (!planet) continue;
          const angle = (i / Math.max(1, n)) * Math.PI * 2;
          const r = ringR(i);
          const px = p.x + Math.cos(angle) * r;
          const py = p.y + Math.sin(angle) * r;
          ctx.fillStyle = planet.ownerEmpireId !== null ? '#7acbff' : '#4a5a7a';
          ctx.beginPath();
          ctx.arc(px, py, planetDotRadius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }

  // v4M6.2 — strategic overlay layer (per `docs/MAP.md §3`). No-op when
  // `ui.galaxyOverlay === 'none'`. Drawn after all star bodies + halos
  // so the per-system tint reads on top, and before ships + per-system
  // indicators so those stay visible on top (per the §3.4 z-order lock).
  drawGalaxyOverlay(ctx, state, ui, vp, starRadius);

  // v4M6.3a — Jump Area destination picker. Gated on: galaxy view +
  // single-source player-owned fleet selected + cursor hovering a
  // destination different from the source. Reuses `outOfRange` so
  // the picker ring respects the v4M5.3b3e range-cap check. Drawn
  // after the overlay layer so the empire-coloured ring + arrival
  // marker read cleanly on top of the heat-map tints, and before
  // ships + indicators so those stay visible on top.
  drawGalaxyJumpTargetPicker(ctx, state, ui, vp, starRadius, outOfRange);

  // v4M12.1 — Jump Prediction Sensors defender arrival-angle preview.
  // Tech-gated (no selection requirement), draws inward chevrons on
  // every destination's Jump Area ring for inbound enemy jumps the
  // viewer can see per `docs/TECHTREE.md §9.1`. Sits AFTER the picker
  // so player-driven chevrons (own arrival marker) layer on top of
  // tech-driven chevrons (enemy preview) when both are active —
  // matters when the player is queuing a jump to a system that
  // already has inbound enemy traffic.
  drawGalaxyArrivalWarnings(ctx, state, ui, vp, starRadius);

  // v4M6.3b — in-flight movement visualisation. Draws path lines (own/
  // teammate solid + ETA badge; visible enemy dotted no-ETA), plus a
  // pulsing source-system ring for charge-up ships. Sits BEFORE the
  // ships pass so the lines + pulses are under the in-flight dots,
  // keeping the dots as the focal point per `docs/MAP.md §4.3.2`.
  drawGalaxyPaths(ctx, state, ui, vp, starRadius);

  // Ships — drawn last so they float above systems. Filtered by fog-of-war:
  // own-empire ships always render; enemy ships only render if the player's
  // teamVision has a row for them. The row's `lastSeenSystemId` anchors
  // enemy chevrons to where they were last observed, so a ship that moved
  // during the grace window doesn't leak its new position to the galaxy
  // view. In v2 the same filter lights up cross-system structure scanners
  // and mid-jump detections automatically (see STRUCTURES v2 plan).
  //
  // Stationary ships stack to the upper-right of their system's star (slight
  // offset per ship so a 3-ship system doesn't look like a single chevron).
  // Mid-jump ships interpolate between their captured origin and target based
  // on each ship's own jumpDurationTotal — so a fast Scout visibly outpaces a
  // slow Cruiser on the same hyperlane. A short halo marks jumping ships.
  // Viewer = player under normal play, first non-player empire when the
  // F10 enemy-POV overlay is on (M8.1-F). Fog / "own ship" treatment flips
  // with it so galaxy view matches system view under the same toggle.
  const viewerId = viewerEmpireId(state, ui);
  const viewerVision = state.teamVision.get(viewerId);

  // MFix5.6 — per-system aggregate counts replace the per-ship chevron
  // stack. Mid-jump ships still render inline as hyperlane dots; they
  // don't belong to any system tally.
  // MFix6.4 — counts are per-empire-per-system so the panel renders
  // one icon row in each empire's color with their count, rather than
  // a single neutral icon. Outer key = systemId; inner key = empireId.
  // v4M4.e.1b — 3 category platforms retired, replaced by 6 subType
  // platforms. MFix42.2 (2026-06-01) — Missile Weapons Platform removed
  // per playtest feedback (redundant with Torpedo + ship-mounted
  // missiles); remaining 5 platforms match the "<X> Weapons Platform"
  // pattern.
  const DEFENCE_PLATFORM_CHASSIS = new Set([
    'Pulse Weapons Platform',
    'Beam Weapons Platform',
    'Railgun Weapons Platform',
    'Burst Weapons Platform',
    'Torpedo Weapons Platform',
  ]);
  const shipsByEmpireBySystem = new Map<SystemId, Map<EmpireId, number>>();
  const platformsByEmpireBySystem = new Map<SystemId, Map<EmpireId, number>>();
  function bumpEmpire(
    map: Map<SystemId, Map<EmpireId, number>>,
    sysId: SystemId,
    empireId: EmpireId,
  ): void {
    let inner = map.get(sysId);
    if (!inner) {
      inner = new Map();
      map.set(sysId, inner);
    }
    inner.set(empireId, (inner.get(empireId) ?? 0) + 1);
  }

  for (const ship of state.ships.values()) {
    const isOwn = ship.ownerEmpireId === viewerId;
    const rec = isOwn || ui.devRevealAll ? null : viewerVision?.get(ship.id);
    if (!isOwn && !ui.devRevealAll && !rec) continue;
    // Ghosts (seenVia=null) are stale memory; don't bump live counts.
    const ghost = !isOwn && rec?.seenVia === null;
    const empire = state.empires.get(ship.ownerEmpireId);
    const color = empire?.color ?? '#ff8f8f';

    let screenX: number;
    let screenY: number;
    let jumping = false;

    if (!isOwn && !ui.devRevealAll) {
      // Enemy — anchor to the last-seen system, not the ship's live
      // systemId. This preserves hidden info during the grace window.
      const lastSeenSys = rec!.lastSeenSystemId;
      const sys = state.systems.get(lastSeenSys);
      if (!sys) continue;
      if (!ghost) {
        bumpEmpire(shipsByEmpireBySystem, lastSeenSys, ship.ownerEmpireId);
        if (DEFENCE_PLATFORM_CHASSIS.has(ship.chassis)) {
          bumpEmpire(platformsByEmpireBySystem, lastSeenSys, ship.ownerEmpireId);
        }
      }
      // Skip the per-ship draw — aggregate render below handles this.
      continue;
    } else if (ship.systemId !== null) {
      const sys = state.systems.get(ship.systemId);
      if (!sys) continue;
      bumpEmpire(shipsByEmpireBySystem, ship.systemId, ship.ownerEmpireId);
      if (DEFENCE_PLATFORM_CHASSIS.has(ship.chassis)) {
        bumpEmpire(platformsByEmpireBySystem, ship.systemId, ship.ownerEmpireId);
      }
      // Skip per-ship draw; aggregate below.
      continue;
    } else if (
      ship.jumpOriginSystemId !== null &&
      ship.jumpTargetSystemId !== null &&
      ship.jumpDurationTotal > 0
    ) {
      const origin = state.systems.get(ship.jumpOriginSystemId);
      const target = state.systems.get(ship.jumpTargetSystemId);
      if (!origin || !target) continue;
      const t = 1 - ship.jumpTicksRemaining / ship.jumpDurationTotal;
      const wx = origin.x + (target.x - origin.x) * t;
      const wy = origin.y + (target.y - origin.y) * t;
      const p = worldToScreen(ui.galaxyCamera, vp, { x: wx, y: wy });
      screenX = p.x;
      screenY = p.y;
      jumping = true;
    } else {
      continue;
    }

    if (jumping) {
      // §8.2: in-transit ships render as a 3 px race-accent (here: empire-
      // color) dot with a soft halo to stand out against the hyperlane.
      // Player-owned transits get a faint 1 px trail behind them showing
      // recent travel; enemy transits don't (per §8.2 — only player units
      // give themselves away). Trail length is a fixed 16 screen-px;
      // longer would overlap the next system's halo on short lanes.
      ctx.save();
      // Soft halo first so it sits beneath the dot.
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.arc(screenX, screenY, 7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();

      // Player-only trail.
      if (isOwn && ship.jumpOriginSystemId !== null && ship.jumpTargetSystemId !== null) {
        const origin = state.systems.get(ship.jumpOriginSystemId);
        const target = state.systems.get(ship.jumpTargetSystemId);
        if (origin && target) {
          const op = worldToScreen(ui.galaxyCamera, vp, { x: origin.x, y: origin.y });
          const tp = worldToScreen(ui.galaxyCamera, vp, { x: target.x, y: target.y });
          const ldx = tp.x - op.x;
          const ldy = tp.y - op.y;
          const llen = Math.hypot(ldx, ldy) || 1;
          const trailLen = Math.min(16, llen * 0.4);
          const tailX = screenX - (ldx / llen) * trailLen;
          const tailY = screenY - (ldy / llen) * trailLen;
          ctx.save();
          const trailGrad = ctx.createLinearGradient(screenX, screenY, tailX, tailY);
          trailGrad.addColorStop(0, color);
          trailGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
          ctx.strokeStyle = trailGrad;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(screenX, screenY);
          ctx.lineTo(tailX, tailY);
          ctx.stroke();
          ctx.restore();
        }
      }

      // 3 px filled dot at race-accent (here: empire) colour.
      ctx.save();
      if (ghost) ctx.globalAlpha = 0.45;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(screenX, screenY, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      continue;
    }
    // MFix5.6 — in-system ships are aggregated into per-system counts
    // above and rendered as icon+number rows below; the per-ship
    // chevron stack is gone.
  }

  // MFix5.6 — per-system aggregate indicators (replaces the chevron
  // stack). Rows of icon+count to the right of each star.
  // MFix6.4 — one row per (empire, category) pair, in the empire's
  // color. Categories: ships, planets, defence platforms.
  // MFix6.6 — anchor the indicator block past the (now-larger) star
  // sprite edge rather than at a fixed offset from centre.
  drawPerSystemIndicators(
    ctx,
    state,
    ui,
    vp,
    shipsByEmpireBySystem,
    platformsByEmpireBySystem,
    starRadius,
  );

  ctx.restore();
}

/** Gray used for the neutral-planet icon — matches the unowned planet-dot tone. */
const NEUTRAL_ICON_COLOR = 'rgba(160, 175, 200, 0.85)';

/**
 * Per-system aggregate indicators (MFix5.6 + MFix6.4). Renders one
 * icon+count row per (empire, category) pair beside each star, with
 * each empire's row in their `color`. Rows are skipped when the count
 * is zero so a quiet system doesn't clutter the view.
 *
 * MFix6.3 fog-of-war: only renders for systems the viewer has scouted
 * (or all systems when the F10 devRevealAll cheat is active).
 *
 * Within each system, rows are clustered by category (all ship rows,
 * then planet rows, then defence rows) so the player can scan by
 * category. Within a category, rows are ordered by empire id for
 * determinism. Neutral planets appear as a separate gray row beneath
 * the empire-owned planet rows.
 */
function drawPerSystemIndicators(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  shipsByEmpireBySystem: Map<SystemId, Map<EmpireId, number>>,
  platformsByEmpireBySystem: Map<SystemId, Map<EmpireId, number>>,
  starRadius: number,
): void {
  // Per-system planet ownership counts. Empire-owned: keyed by empire id.
  // Neutral (ownerEmpireId === null): keyed in a separate map. Walking
  // the full planet table once per frame is cheap at v1 scale (~30-60
  // planets total).
  const planetsByEmpireBySystem = new Map<SystemId, Map<EmpireId, number>>();
  const neutralPlanetsBySystem = new Map<SystemId, number>();
  for (const planet of state.planets.values()) {
    if (planet.ownerEmpireId === null) {
      neutralPlanetsBySystem.set(
        planet.systemId,
        (neutralPlanetsBySystem.get(planet.systemId) ?? 0) + 1,
      );
      continue;
    }
    let inner = planetsByEmpireBySystem.get(planet.systemId);
    if (!inner) {
      inner = new Map();
      planetsByEmpireBySystem.set(planet.systemId, inner);
    }
    inner.set(planet.ownerEmpireId, (inner.get(planet.ownerEmpireId) ?? 0) + 1);
  }

  ctx.save();
  // MFix6.6 fix-up #2 — count text trimmed 18 → 16 px after icons were
  // shrunk 25 %. Keeps visual weight balanced across the row.
  ctx.font = '16px Inter, system-ui, "Segoe UI", sans-serif';
  ctx.textBaseline = 'middle';

  // MFix6.3 — Aggregate indicators are gated on the viewer empire's
  // scouted set. Unscouted systems show no counts; you'd be inferring
  // troop levels otherwise. devRevealAll bypasses for the F10 cheat.
  const viewerForFog = state.empires.get(viewerEmpireId(state, ui));

  for (const sys of state.systems.values()) {
    const visible =
      ui.devRevealAll || (viewerForFog?.scoutedSystemIds.has(sys.id) ?? false);
    if (!visible) continue;

    const shipCounts = shipsByEmpireBySystem.get(sys.id);
    const platformCounts = platformsByEmpireBySystem.get(sys.id);
    const empirePlanetCounts = planetsByEmpireBySystem.get(sys.id);
    const neutralPlanets = neutralPlanetsBySystem.get(sys.id) ?? 0;

    const hasAny =
      (shipCounts && shipCounts.size > 0) ||
      (platformCounts && platformCounts.size > 0) ||
      (empirePlanetCounts && empirePlanetCounts.size > 0) ||
      neutralPlanets > 0;
    if (!hasAny) continue;

    const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
    // MFix6.6 fix-up #2 — icons trimmed 25 %, so row height drops
    // 24 → 20 px and the count-text offset drops 18 → 14 px (still
    // clears the widest icon — the ship triangle, now 18 px wide).
    const ROW_HEIGHT = 20;
    const COUNT_TEXT_DX = 14;
    const ax = p.x + starRadius + 16;
    let ay = p.y - ROW_HEIGHT / 2;

    // Ship rows (one per empire in id order).
    if (shipCounts) {
      for (const [empireId, count] of sortedEmpireEntries(shipCounts)) {
        if (count <= 0) continue;
        const color = state.empires.get(empireId)?.color ?? NEUTRAL_ICON_COLOR;
        drawShipIcon(ctx, ax, ay, color);
        ctx.fillStyle = color;
        ctx.fillText(String(count), ax + COUNT_TEXT_DX, ay);
        ay += ROW_HEIGHT;
      }
    }

    // Planet rows — empire-owned first, then a neutral row at the end.
    if (empirePlanetCounts) {
      for (const [empireId, count] of sortedEmpireEntries(empirePlanetCounts)) {
        if (count <= 0) continue;
        const color = state.empires.get(empireId)?.color ?? NEUTRAL_ICON_COLOR;
        drawPlanetIcon(ctx, ax, ay, color);
        ctx.fillStyle = color;
        ctx.fillText(String(count), ax + COUNT_TEXT_DX, ay);
        ay += ROW_HEIGHT;
      }
    }
    if (neutralPlanets > 0) {
      drawPlanetIcon(ctx, ax, ay, NEUTRAL_ICON_COLOR);
      ctx.fillStyle = NEUTRAL_ICON_COLOR;
      ctx.fillText(String(neutralPlanets), ax + COUNT_TEXT_DX, ay);
      ay += ROW_HEIGHT;
    }

    // Defence platform rows (one per empire in id order).
    if (platformCounts) {
      for (const [empireId, count] of sortedEmpireEntries(platformCounts)) {
        if (count <= 0) continue;
        const color = state.empires.get(empireId)?.color ?? NEUTRAL_ICON_COLOR;
        drawDefenceIcon(ctx, ax, ay, color);
        ctx.fillStyle = color;
        ctx.fillText(String(count), ax + COUNT_TEXT_DX, ay);
        ay += ROW_HEIGHT;
      }
    }
  }
  ctx.restore();
}

/** Iterate empire-id-keyed entries in ascending id order — deterministic across frames. */
function sortedEmpireEntries(
  m: Map<EmpireId, number>,
): Array<[EmpireId, number]> {
  return [...m.entries()].sort((a, b) => a[0] - b[0]);
}

// MFix6.6 fix-up #2 — icon shapes trimmed 25 %: triangle 24×18 → 18×14,
// circle r=9 → r=7, square 18×18 → 14×14. Centred on (x, y); rendered
// with the empire's color (or NEUTRAL_ICON_COLOR for neutral planets).
function drawShipIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x, y - 7);
  ctx.lineTo(x + 9, y + 7);
  ctx.lineTo(x - 9, y + 7);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawPlanetIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawDefenceIcon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.strokeRect(x - 7, y - 7, 14, 14);
  ctx.restore();
}

/**
 * v4M5.3b3e — compute the set of system ids that lie beyond the
 * currently selected fleet's effective hyperdrive range, measured
 * from the source system. Empty set when no player ships are
 * selected or when the selection spans multiple systems (no single
 * source). The fleet's effective range is the min over selected
 * ships of `template.hyperdriveRange × hyperdriveRangeMultiplier`
 * — same formula as `applyJumpShips` so the render gate matches
 * the applier gate exactly.
 */
function computeOutOfRangeSystemIds(state: GameState, ui: UiState): Set<SystemId> {
  const out = new Set<SystemId>();
  if (ui.selectedShipIds.size === 0) return out;
  let sourceSystemId: SystemId | null = null;
  let empire = state.empires.get(state.playerEmpireId);
  if (!empire) return out;
  let fleetRange = Number.POSITIVE_INFINITY;
  for (const id of ui.selectedShipIds) {
    const ship = state.ships.get(id);
    if (!ship) continue;
    if (ship.ownerEmpireId !== state.playerEmpireId) continue;
    if (ship.systemId === null) continue;
    if (sourceSystemId === null) sourceSystemId = ship.systemId;
    else if (sourceSystemId !== ship.systemId) return out;
    const tmpl = state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
    if (!tmpl) continue;
    const r = tmpl.hyperdriveRange * hyperdriveRangeMultiplier(state, empire);
    if (r < fleetRange) fleetRange = r;
  }
  if (sourceSystemId === null) return out;
  if (!Number.isFinite(fleetRange)) return out;
  const src = state.systems.get(sourceSystemId);
  if (!src) return out;
  for (const sys of state.systems.values()) {
    if (sys.id === sourceSystemId) continue;
    if (Math.hypot(sys.x - src.x, sys.y - src.y) > fleetRange) out.add(sys.id);
  }
  return out;
}
